import { useId, useState } from 'react';

import { Button, Checkbox, Dialog, Field, Input, InputPassword, Select } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { maLoi } from '../../../../lib/errors';
import { useToast } from '../../../../lib/toast';
import { HopXacNhanThaoTac } from '../../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { useRolesOpenCore, useSuaUserOpenCore, useTaoUserOpenCore } from '../../hooks/useDanhBaOpenCore';
import {
  canXacNhanAdmin,
  formTuUser,
  GIOI_HAN_USER,
  kiemUser,
  MAT_KHAU_TOI_DA,
  MAT_KHAU_TOI_THIEU,
  thanUser,
  type FormUser,
  type LoiUser,
} from '../../nghiep-vu/danh-ba';
import type { UserOpenCore } from '../../types';

const CAN_XAC_NHAN = 'RPA_OPENCORE_ADMIN_CONFIRM_REQUIRED';

/**
 * Tạo hoặc sửa User OpenCore (`OpenCoreUserFormDialog` của native). Tạo không gán role — gán sau bằng Sửa. Ô role chỉ
 * hiện khi có `rpa:opencore-role:update` (máy chủ đòi quyền này khi gửi `roleIds`). Thêm vào role `admins` thì hỏi lại;
 * máy chủ vẫn trả `409 RPA_OPENCORE_ADMIN_CONFIRM_REQUIRED` khi thiếu xác nhận, lúc đó cũng hỏi lại rồi gửi lại.
 */
export function HopUser({ goc, ganRoleDuoc, onClose }: { goc: UserOpenCore | null; ganRoleDuoc: boolean; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const taoMoi = !goc;
  const hienRole = !taoMoi && ganRoleDuoc;
  const roles = useRolesOpenCore(hienRole);
  const tao = useTaoUserOpenCore();
  const sua = useSuaUserOpenCore();
  const dang = goc ? sua : tao;
  const [dau] = useState(() => formTuUser(goc));
  const [v, setV] = useState<FormUser>(dau);
  const [loi, setLoi] = useState<LoiUser>({});
  const [hoiAdmin, setHoiAdmin] = useState(false);
  // Không biết User đang thuộc role nào thì không cho sửa role: gửi đi sẽ gỡ nhầm.
  const bietRole = !goc || goc.rolesStatus !== 'unavailable';

  const dat = <K extends keyof FormUser>(k: K, gt: FormUser[K]) => {
    setV((cu) => ({ ...cu, [k]: gt }));
    dang.reset();
  };

  const gui = (xacNhanAdmin: boolean) => {
    const thay = kiemUser(v, taoMoi);
    setLoi(thay);
    if (Object.keys(thay).length > 0 || dang.isPending) return;
    if (!xacNhanAdmin && hienRole && canXacNhanAdmin(v.roleIds, dau.roleIds, roles.data)) {
      setHoiAdmin(true);
      return;
    }
    const than = thanUser(v, { taoMoi, roleCu: dau.roleIds, bietRole: hienRole && bietRole, xacNhanAdmin });
    const xong = {
      onSuccess: () => {
        toast.success(t(taoMoi ? 'rpaDanhBa.form.daTao' : 'rpaDanhBa.form.daLuu', { ten: than.name }));
        setHoiAdmin(false);
        onClose();
      },
      onError: (e: unknown) => {
        if (maLoi(e) === CAN_XAC_NHAN && !xacNhanAdmin) setHoiAdmin(true);
        else setHoiAdmin(false);
      },
    };
    if (goc) sua.mutate({ id: goc.id, than }, xong);
    else tao.mutate(than, xong);
  };

  const nhanLoi = (k: keyof LoiUser): string | undefined => {
    const m = loi[k];
    if (!m) return undefined;
    if (k === 'password') return t(m === 'long' ? 'rpaDanhBa.form.loiMatKhauDai' : 'rpaDanhBa.form.loiMatKhauNgan', { min: MAT_KHAU_TOI_THIEU, max: MAT_KHAU_TOI_DA });
    if (k === 'confirm') return t('rpaDanhBa.form.loiXacNhan');
    if (k === 'email') return t('rpaDanhBa.form.loiEmail');
    if (k === 'username') return t(m === 'invalid' ? 'rpaDanhBa.form.loiUsername' : 'rpaDanhBa.form.loiUsernameThieu');
    return t('rpaDanhBa.form.loiTen');
  };

  const loiChung = dang.error && maLoi(dang.error) !== CAN_XAC_NHAN ? dichLoi(dang.error) : null;

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        width={720}
        destroyOnHidden
        title={goc ? t('rpaDanhBa.form.suaTieuDe', { ten: goc.name || goc.username }) : t('rpaDanhBa.form.taoTieuDe')}
        footer={
          <div className="space-y-3">
            {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
            <div className="flex flex-wrap justify-end gap-3">
              <Button variant="outline" size="md" onClick={onClose}>
                {t('chung.huy')}
              </Button>
              <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dang.isPending && !hoiAdmin}>
                {dang.isPending ? t('chung.dangLuu') : t(taoMoi ? 'rpaDanhBa.form.tao' : 'rpaDanhBa.form.luu')}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id={formId}
          noValidate
          className="grid grid-cols-1 gap-4 pt-2 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            gui(false);
          }}
        >
          <Field label={t('rpaDanhBa.form.ten')} required error={nhanLoi('name')}>
            {(p) => <Input {...p} autoFocus autoComplete="off" maxLength={GIOI_HAN_USER.ten} value={v.name} onChange={(e) => dat('name', e.target.value)} />}
          </Field>
          <Field label={t('rpaDanhBa.form.username')} required hint={t('rpaDanhBa.form.usernameGoiY')} error={nhanLoi('username')}>
            {(p) => (
              <Input
                {...p}
                autoComplete="off"
                spellCheck={false}
                maxLength={GIOI_HAN_USER.username}
                value={v.username}
                onChange={(e) => dat('username', e.target.value)}
              />
            )}
          </Field>
          <Field label={t('rpaDanhBa.form.email')} className="sm:col-span-2" error={nhanLoi('email')}>
            {(p) => (
              <Input {...p} type="email" autoComplete="off" spellCheck={false} maxLength={GIOI_HAN_USER.email} value={v.email} onChange={(e) => dat('email', e.target.value)} />
            )}
          </Field>
          {taoMoi && (
            <>
              <Field label={t('rpaDanhBa.form.matKhau')} required hint={t('rpaDanhBa.form.matKhauGoiY', { min: MAT_KHAU_TOI_THIEU })} error={nhanLoi('password')}>
                {(p) => (
                  <InputPassword {...p} autoComplete="new-password" maxLength={MAT_KHAU_TOI_DA} value={v.password} onChange={(e) => dat('password', e.target.value)} />
                )}
              </Field>
              <Field label={t('rpaDanhBa.form.xacNhanMatKhau')} required error={nhanLoi('confirm')}>
                {(p) => (
                  <InputPassword {...p} autoComplete="new-password" maxLength={MAT_KHAU_TOI_DA} value={v.confirm} onChange={(e) => dat('confirm', e.target.value)} />
                )}
              </Field>
            </>
          )}
          <div className="flex flex-wrap gap-4 sm:col-span-2">
            <Checkbox checked={v.enabled} onChange={(e) => dat('enabled', e.target.checked)}>
              {t('rpaDanhBa.form.enabled')}
            </Checkbox>
            <Checkbox checked={v.validated} onChange={(e) => dat('validated', e.target.checked)}>
              {t('rpaDanhBa.form.validated')}
            </Checkbox>
            <Checkbox checked={v.emailValidated} onChange={(e) => dat('emailValidated', e.target.checked)}>
              {t('rpaDanhBa.form.emailValidated')}
            </Checkbox>
          </div>
          {hienRole && (
            <Field
              label={t('rpaDanhBa.form.role')}
              className="sm:col-span-2"
              hint={!bietRole ? t('rpaDanhBa.form.roleChuaBiet') : roles.isError ? dichLoi(roles.error) : t('rpaDanhBa.form.roleGoiY')}
            >
              {(p) => (
                <Select<string[]>
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  className="w-full"
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('rpaDanhBa.form.chonRole')}
                  loading={roles.isPending}
                  disabled={!bietRole || roles.isError}
                  value={v.roleIds}
                  onChange={(ids) => dat('roleIds', ids)}
                  options={(roles.data ?? []).map((r) => ({
                    value: r.id,
                    label: r.managedBy === 'license' ? `${r.name} ${t('rpaDanhBa.form.quaLicense')}` : r.name,
                    disabled: r.managedBy === 'license',
                  }))}
                />
              )}
            </Field>
          )}
        </form>
      </Dialog>
      {hoiAdmin && (
        <HopXacNhanThaoTac
          open
          nguyHiem
          tieuDe={t('rpaDanhBa.admin.tieuDe')}
          than={t('rpaDanhBa.admin.than', { ten: v.name || v.username })}
          nhanXacNhan={t('rpaDanhBa.admin.nut')}
          dangChay={dang.isPending}
          loi={null}
          onClose={() => setHoiAdmin(false)}
          onXacNhan={() => gui(true)}
        />
      )}
    </>
  );
}
